/**
 * DataProfilingChart.tsx
 * Sidebar-navigated profiling charts (value type, length, top values, completeness).
 * Redesigned — uses shared GenWizard light theme.
 * All chart logic & data transformations are 100% preserved.
 */

import React, { Component } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend, ArcElement,
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGauge, faRuler, faChartBar } from '@fortawesome/free-solid-svg-icons';

import { T, BAR_PALETTE, cls, injectTheme } from '../theme/theme';
import { AGDataGrid } from '../common/AGDataGrid';
import Sidebar from '../common/Sidebar';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ─── Types ────────────────────────────────────────────────────────────────────
type dfProps = {
  data: any;
  recordStore: Record<string, any>;
};

type dfState = {
  activeKey: string;
  sideBarCollapsed: boolean;
  selectedChartSamples: any[];
};

// ─── Shared chart defaults ────────────────────────────────────────────────────
const baseTooltip = {
  backgroundColor: T.bgCard,
  titleColor:      T.textPrimary,
  bodyColor:       T.textMuted,
  borderColor:     T.border,
  borderWidth:     1,
};

const baseTick = { font: { size: 11 }, color: T.textMuted };
const baseGrid = { color: T.border };

// ─── Component ────────────────────────────────────────────────────────────────
export default class DataProfilingChart extends Component<dfProps, dfState> {

  state: dfState = {
    activeKey:            'dataType',
    sideBarCollapsed:     true,
    selectedChartSamples: [],
  };

  componentDidMount() { injectTheme(); }

  toggleSidebar = () => this.setState(prev => ({ sideBarCollapsed: !prev.sideBarCollapsed }));

  // ── Chart data builders (logic unchanged) ─────────────────────────────────
  getLengthChartData() {
    return {
      labels:   this.props.data.data_length_distribution.map((d: any) => d.length_range),
      datasets: [{
        label:           '% Records',
        data:            this.props.data.data_length_distribution.map((d: any) => d.pct),
        backgroundColor: BAR_PALETTE[0],
        borderRadius:    4,
        borderSkipped:   false as false,
      }],
    };
  }

  getTopValuesChartData() {
    const entries = Object.entries(this.props.data.distinct_values);
    return {
      labels:   entries.map(([k]) => k),
      datasets: [{
        label:           'Top Values',
        data:            entries.map(([, v]) => v as number),
        backgroundColor: entries.map((_, i) => BAR_PALETTE[i % BAR_PALETTE.length] as string),
        borderRadius:    4,
        borderSkipped:   false as false,
      }],
    };
  }

  getCompletenessChartData() {
    const { count, null_count } = this.props.data;
    return {
      labels: ['Present', 'Null'],
      datasets: [{ data: [count - null_count, null_count], backgroundColor: [T.green, T.red] }],
    };
  }

  getValueTypeChartData() {
    const { value_type_counts = {} } = this.props.data;
    const labelMap: Record<string, string> = {
      int_like: 'Integer', float_like: 'Decimal', date_like: 'Date',
      boolean_like: 'Boolean', string_like: 'Text',
    };
    const entries = Object.entries(value_type_counts as Record<string, number>).filter(([, v]) => v > 0);
    return {
      labels:   entries.map(([k]) => labelMap[k] || k),
      datasets: [{ data: entries.map(([, v]) => v), backgroundColor: BAR_PALETTE.slice(0, entries.length) }],
    };
  }

  // ── Sample record helpers ─────────────────────────────────────────────────
  filterSampleRecords = (ids: string[]) =>
    ids.map(id => this.props.recordStore[id]).filter(Boolean);

  getSampleGridColumns = () => {
    const rec = this.state.selectedChartSamples?.[0];
    const profiledCol = this.props.data?.column_name;
    if (!rec) return [];
    const keys = [profiledCol, ...Object.keys(rec).filter(k => k !== profiledCol)];
    return keys.map(k => ({ field: k, headerName: k, cellClass: k === profiledCol ? 'highlighted-column' : '' }));
  };

  // ── Chart renders ─────────────────────────────────────────────────────────
  renderValueTypeChart() {
    return (
      <div className="gw-chart-card">
        <div className="gw-chart-hdr"><div className="gw-chart-title">Value Type Distribution</div></div>
        <div className="gw-chart-body" style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 220, height: 220 }}>
            <Pie data={this.getValueTypeChartData()} options={{
              responsive: false, maintainAspectRatio: false,
              plugins: {
                legend: { position: 'right' },
                tooltip: {
                  ...baseTooltip,
                  callbacks: {
                    label: (item: any) => {
                      const count = item.raw as number;
                      const total = this.props.data.count || 1;
                      return `${item.label}: ${count} (${((count / total) * 100).toFixed(1)}%)`;
                    },
                  },
                },
              },
              onClick: (_, elements, chart) => {
                if (!elements.length) return;
                const label = chart.data.labels?.[elements[0].index];
                const reverseMap: Record<string, string> = {
                  Integer: 'int_like', Decimal: 'float_like', Date: 'date_like',
                  Boolean: 'boolean_like', Text: 'string_like',
                };
                const key = reverseMap[label as string];
                const ids = this.props.data.value_type_record_samples?.[key] || [];
                this.setState({ selectedChartSamples: this.filterSampleRecords(ids) });
              },
            }} />
          </div>
        </div>
      </div>
    );
  }

  renderCompletenessChart() {
    return (
      <div className="gw-chart-card">
        <div className="gw-chart-hdr"><div className="gw-chart-title">Data Completeness</div></div>
        <div className="gw-chart-body" style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 220, height: 220 }}>
            <Pie data={this.getCompletenessChartData()} options={{
              responsive: false, maintainAspectRatio: false,
              plugins: { legend: { position: 'bottom' }, tooltip: baseTooltip },
              onClick: (_, elements, chart) => {
                if (!elements.length) return;
                const label = chart.data.labels?.[elements[0].index];
                const key   = label === 'Present' ? 'non_null_samples' : 'null_samples';
                const ids   = this.props.data.null_non_null_samples?.[key] || [];
                this.setState({ selectedChartSamples: this.filterSampleRecords(ids) });
              },
            }} />
          </div>
        </div>
      </div>
    );
  }

  renderLengthChart() {
    return (
      <div className="gw-chart-card">
        <div className="gw-chart-hdr">
          <div className="gw-chart-title">Data Length Distribution</div>
          <span style={{ fontSize: 11, color: T.textMuted }}>{this.props.data.column_name}</span>
        </div>
        <div className="gw-chart-body">
          <div style={{ height: 300 }}>
            <Bar data={this.getLengthChartData()} options={{
              responsive: true, maintainAspectRatio: false,
              scales: { x: { grid: { display: false }, ticks: baseTick }, y: { beginAtZero: true, grid: baseGrid, ticks: baseTick } },
              plugins: { legend: { display: false }, tooltip: baseTooltip },
              onClick: (_, elements, chart) => {
                if (!elements.length) return;
                const label = chart.data.labels?.[elements[0].index] as string;
                const len   = label?.split('-')[0];
                const ids   = this.props.data.data_length_record_samples?.[label]
                           || this.props.data.data_length_record_samples?.[len] || [];
                this.setState({ selectedChartSamples: this.filterSampleRecords(ids) });
              },
            }} />
          </div>
        </div>
      </div>
    );
  }

  renderTopValuesChart() {
    const entries = Object.entries(this.props.data.distinct_values);
    const barH    = Math.max(entries.length * 28, 260);
    return (
      <div className="gw-chart-card">
        <div className="gw-chart-hdr"><div className="gw-chart-title">Top Frequent Values</div></div>
        <div className="gw-chart-body">
          <div style={{ height: 300, overflowY: 'auto' }} className="gw-scroll">
            <div style={{ height: barH }}>
              <Bar data={this.getTopValuesChartData()} options={{
                responsive: true, indexAxis: 'y', maintainAspectRatio: false,
                scales: { x: { beginAtZero: true, grid: baseGrid, ticks: { ...baseTick, precision: 0 } }, y: { ticks: { autoSkip: false, ...baseTick } } },
                plugins: { legend: { display: false }, tooltip: baseTooltip },
                onClick: (_, elements) => {
                  if (!elements.length) return;
                  const key = Object.keys(this.props.data.distinct_values)[elements[0].index];
                  const samples = Object.entries(this.props.recordStore)
                    .filter(([, rec]) => rec[this.props.data.column_name]?.toString() === key)
                    .map(([, rec]) => rec);
                  this.setState({ selectedChartSamples: samples });
                },
              }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderTabContent() {
    switch (this.state.activeKey) {
      case 'dataType':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {this.renderValueTypeChart()}
            {this.renderCompletenessChart()}
          </div>
        );
      case 'length': return this.renderLengthChart();
      case 'value':  return this.renderTopValuesChart();
      default:       return null;
    }
  }

  render() {
    const { sideBarCollapsed, selectedChartSamples, activeKey } = this.state;
    const sidebarItems = [
      { key: 'dataType', label: 'Data Type',         icon: faGauge },
      { key: 'length',   label: 'Data Length',        icon: faRuler },
      { key: 'value',    label: 'Data Distribution',  icon: faChartBar },
    ];

    return (
      <div className="gw-root">

        {/* Recommended type pill */}
        {this.props.data.recommended_type && (
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="gw-label">Recommended Type</span>
            <span className="gw-badge gw-badge-brand">{this.props.data.recommended_type}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 0 }}>
          {/* Sidebar nav */}
          <Sidebar
            items={sidebarItems}
            activeKey={activeKey}
            onSelect={(k: string) => this.setState({ activeKey: k, selectedChartSamples: [] })}
            collapsed={sideBarCollapsed}
            onToggleCollapse={this.toggleSidebar}
            theme="grayBordered"
            width={180}
            fontSize="13px"
            compact={false}
          />

          {/* Chart area */}
          <div style={{ flex: 1, marginLeft: 12 }}>
            {this.renderTabContent()}

            {selectedChartSamples.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="gw-section-hdr">Sample Records</div>
                <div style={{ height: 280 }}>
                  <AGDataGrid
                    title="Sample Data"
                    columnDefs={this.getSampleGridColumns()}
                    rowData={selectedChartSamples}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}